/**
 * PATH: packages/job-hub/src/jobs/qa/inlineRename.ts
 * FILE_ID: JOBHUB-QA-RENAME-001
 * PURPOSE: Pure decision helper for inline-renaming QA entities (QA list /
 *          area). Given the raw input and the current name, decide whether a
 *          rename should be persisted and what value to send.
 * WHY:     Operator (2026-06-18): "I can't change names" — the QA list, area
 *          and item names were read-only after creation. Centralising the
 *          trim/changed/valid decision keeps the React rename UI declarative
 *          and lets us unit-test the "should we PATCH?" rule without a DOM.
 * FLOW:
 * ┌──────────────┐   ┌─────────────────────────┐   ┌──────────────────────┐
 * │ raw + current│ → │ trim → valid? → changed? │ → │ { value, valid,      │
 * │              │   │                          │   │   changed }          │
 * └──────────────┘   └─────────────────────────┘   └──────────────────────┘
 */

export interface RenameDecision {
  /** The trimmed value to persist. */
  value: string
  /** Non-empty after trimming. */
  valid: boolean
  /** Valid AND different from the current (trimmed) name. */
  changed: boolean
}

/**
 * Decide whether an inline rename should be saved. Pure I->O: only a non-empty
 * value that differs from the current name is a real change worth a PATCH;
 * everything else is a no-op so we never blank a name or fire a redundant
 * request (e.g. open the editor and hit Enter without typing).
 */
export function decideRename(raw: string, current: string): RenameDecision {
  const value = (raw ?? '').trim()
  const valid = value.length > 0
  const changed = valid && value !== (current ?? '').trim()
  return { value, valid, changed }
}
